/**
 * For the blocking mode hook, we can't just `useSource()` if we want to
 * get route transitions which wait for the next route to load, because
 * useSource() (and useSubscription()) always immediately sets state --
 * even if we provide a `startTransition` function.
 *
 * In order to subscribe to a source *and maybe wait before saving the received
 * value to React state*, we'll need custom subscription logic. That's what
 * this hook provides.
 */
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { delay } from 'retil-support';
import { getSnapshot, hasSnapshot, mergeLatest, subscribe, } from 'retil-source';
import { UseRouterDefaultsContext } from '../routerContext';
import { waitForResponse } from '../routerUtils';
export var useRouterSourceBlocking = function (source, options) {
    if (options === void 0) { options = {}; }
    var defaultTransitionTimeoutMs = useContext(UseRouterDefaultsContext)
        .transitionTimeoutMs;
    var _a = options.transitionTimeoutMs, transitionTimeoutMs = _a === void 0 ? defaultTransitionTimeoutMs : _a;
    var latestSource = useMemo(function () {
        return mergeLatest(source, function (latestSnapshot, isSuspended) { return [latestSnapshot, isSuspended]; });
    }, [source]);
    var _b = useState(function () { return ({
        currentSnapshot: getSnapshot(latestSource)[0],
        pendingSnapshot: null,
        source: latestSource,
        sourcePending: false,
    }); }), state = _b[0], setState = _b[1];
    var hasUnmountedRef = useRef(false);
    useEffect(function () { return function () {
        hasUnmountedRef.current = true;
    }; }, []);
    var handleNewSnapshot = useMemo(function () {
        var hasGotInitialSnapshot = false;
        return function (force) {
            hasGotInitialSnapshot =
                !!force || hasGotInitialSnapshot || hasSnapshot(latestSource);
            if (hasGotInitialSnapshot || transitionTimeoutMs === 0) {
                var _a = getSnapshot(latestSource), snapshot_1 = _a[0], isSuspended = _a[1];
                if (transitionTimeoutMs === 0 ||
                    !snapshot_1.response.pendingSuspenses.length) {
                    // TODO: update these to only update if the snapshots have changed
                    setState({
                        currentSnapshot: snapshot_1,
                        pendingSnapshot: null,
                        source: latestSource,
                        sourcePending: isSuspended,
                    });
                }
                else {
                    setState(function (_a) {
                        var currentSnapshot = _a.currentSnapshot;
                        return ({
                            currentSnapshot: currentSnapshot,
                            pendingSnapshot: snapshot_1,
                            source: latestSource,
                            sourcePending: false,
                        });
                    });
                    Promise.race([waitForResponse(snapshot_1.response)].concat(transitionTimeoutMs === Infinity
                        ? []
                        : [delay(transitionTimeoutMs)])).then(function () {
                        if (!hasUnmountedRef.current) {
                            setState(function (state) {
                                return snapshot_1 === state.pendingSnapshot
                                    ? {
                                        currentSnapshot: snapshot_1,
                                        pendingSnapshot: null,
                                        source: latestSource,
                                        sourcePending: false,
                                    }
                                    : state;
                            });
                        }
                    });
                }
            }
            else {
                setState(function (_a) {
                    var currentSnapshot = _a.currentSnapshot;
                    return ({
                        currentSnapshot: currentSnapshot,
                        pendingSnapshot: null,
                        source: latestSource,
                        sourcePending: true,
                    });
                });
            }
        };
    }, [latestSource, transitionTimeoutMs]);
    if (latestSource !== state.source) {
        handleNewSnapshot();
    }
    useEffect(function () {
        var unsubscribed = false;
        if (!hasSnapshot(latestSource) &&
            transitionTimeoutMs !== Infinity &&
            transitionTimeoutMs === 0) {
            delay(transitionTimeoutMs).then(function () {
                if (!unsubscribed) {
                    handleNewSnapshot(true);
                }
            });
        }
        var unsubscribe = subscribe(latestSource, handleNewSnapshot);
        return function () {
            unsubscribed = true;
            unsubscribe();
        };
    }, [latestSource, handleNewSnapshot, transitionTimeoutMs]);
    return [state.currentSnapshot, !!state.pendingSnapshot || state.sourcePending];
};
